import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxSelectBoxModule, DxButtonModule, DxPopupModule, DxTextBoxModule, DxCheckBoxModule } from 'devextreme-angular';
import { GridProfileService } from '../services/grid-profile.service';
import { GridProfile } from '../models/grid-profile.model';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-grid-profile-toolbar',
  standalone: true,
  imports: [CommonModule, DxSelectBoxModule, DxButtonModule, DxPopupModule, DxTextBoxModule, DxCheckBoxModule],
  templateUrl: './grid-profile-toolbar.component.html',
  styleUrl: './grid-profile-toolbar.component.css'
})
export class GridProfileToolbarComponent implements OnInit {
  @Input() gridKey: string = '';
  @Output() profileLoaded = new EventEmitter<string | null>();

  profiles: GridProfile[] = [];
  selectedProfileId: number | null = null;
  saveAsPopupVisible = false;
  newProfileName = '';
  newProfileIsDefault = false;

  private pendingState: string | null = null;

  constructor(private gridProfileService: GridProfileService) {}

  ngOnInit() {
    this.loadProfiles();
  }

  get selectedProfile(): GridProfile | null {
    return this.profiles.find(p => p.id === this.selectedProfileId) || null;
  }

  onProfileSelected(e: any) {
    const value = e.value;
    if (value === null) {
      this.selectedProfileId = null;
      this.profileLoaded.emit(null);
      return;
    }
    this.selectedProfileId = value;
    const profile = this.selectedProfile;
    if (profile) {
      this.profileLoaded.emit(profile.grid_state);
    }
  }

  saveCurrentState(gridState: string) {
    this.pendingState = gridState;
  }

  async onSaveClick() {
    if (!this.selectedProfile || !this.pendingState) return;
    try {
      await lastValueFrom(
        this.gridProfileService.updateProfile(this.selectedProfile.id, {
          grid_state: this.pendingState
        })
      );
      this.selectedProfile.grid_state = this.pendingState;
      notify({ message: 'Profile saved', width: 300 }, 'success', 2000);
    } catch (error: any) {
      notify({ message: 'Failed to save profile', width: 300 }, 'error', 3000);
    }
  }

  onSaveAsClick() {
    this.newProfileName = '';
    this.newProfileIsDefault = false;
    this.saveAsPopupVisible = true;
  }

  async onSaveAsConfirm() {
    if (!this.newProfileName.trim()) {
      notify({ message: 'Please enter a profile name', width: 300 }, 'warning', 2000);
      return;
    }
    try {
      const result: any = await lastValueFrom(
        this.gridProfileService.createProfile({
          grid_key: this.gridKey,
          profile_name: this.newProfileName.trim(),
          grid_state: this.pendingState || '{}',
          is_default: this.newProfileIsDefault
        })
      );
      this.saveAsPopupVisible = false;
      await this.loadProfiles();
      if (result?.data?.id) {
        this.selectedProfileId = result.data.id;
      }
      notify({ message: 'Profile created', width: 300 }, 'success', 2000);
    } catch (error: any) {
      const msg = error?.error?.data?.[0] || error?.error?.message || 'Failed to create profile';
      notify({ message: msg, width: 300 }, 'error', 3000);
    }
  }

  onSaveAsCancel() {
    this.saveAsPopupVisible = false;
  }

  async onDeleteClick() {
    if (!this.selectedProfile) return;
    const confirmed = await confirm(
      `Are you sure you want to delete the profile "${this.selectedProfile.profile_name}"?`,
      'Delete Profile'
    );
    if (!confirmed) return;
    try {
      await lastValueFrom(
        this.gridProfileService.deleteProfile(this.selectedProfile.id)
      );
      this.selectedProfileId = null;
      await this.loadProfiles();
      this.profileLoaded.emit(null);
      notify({ message: 'Profile deleted', width: 300 }, 'success', 2000);
    } catch (error: any) {
      notify({ message: 'Failed to delete profile', width: 300 }, 'error', 3000);
    }
  }

  private async loadProfiles() {
    try {
      const result: any = await lastValueFrom(
        this.gridProfileService.getProfiles(this.gridKey)
      );
      this.profiles = result?.data || result || [];

      // Auto-select default profile if no profile is currently selected
      if (this.selectedProfileId === null) {
        const defaultProfile = this.profiles.find(p => p.is_default);
        if (defaultProfile) {
          this.selectedProfileId = defaultProfile.id;
          this.profileLoaded.emit(defaultProfile.grid_state);
        }
      }
    } catch (error) {
      console.error('Error loading grid profiles:', error);
      this.profiles = [];
    }
  }
}
